import { useEffect, useRef, type ReactNode } from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/cn";
import { IconButton } from "./IconButton";
import { X } from "./Icon";

interface Props {
  open?: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  size?: "md" | "lg" | "xl";
}

/**
 * Modal primitive with focus trap, Esc-to-close, and elevation-md surface.
 * Trap is intentionally tiny — Maestro never has nested modals.
 */
export function Modal({
  open = true,
  onClose,
  title,
  description,
  footer,
  children,
  size = "md",
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    const focusable = panel?.querySelector<HTMLElement>(
      'input,textarea,select,button,[tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
      if (e.key === "Tab" && panel) {
        const nodes = panel.querySelectorAll<HTMLElement>(
          'input,textarea,select,button,[tabindex]:not([tabindex="-1"])',
        );
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      lastFocusedRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : undefined}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0 bg-[rgb(0_0_0_/_0.5)] backdrop-blur-[2px]"
        onClick={onClose}
      />
      <motion.div
        ref={panelRef}
        initial={{ opacity: 0, y: 8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.98 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          "relative w-full bg-bg-surface rounded-lg shadow-lg border border-border-subtle",
          "flex flex-col max-h-[90vh] overflow-hidden",
          size === "xl"
            ? "max-w-[864px]"
            : size === "lg"
              ? "max-w-[720px]"
              : "max-w-[560px]",
        )}
      >
        {(title || description) && (
          <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3">
            <div className="min-w-0 flex-1">
              {title && (
                <div className="text-heading text-text-primary">{title}</div>
              )}
              {description && (
                <div className="text-small text-text-tertiary mt-1">
                  {description}
                </div>
              )}
            </div>
            <IconButton
              icon={<X />}
              label="Close"
              onClick={onClose}
              size="sm"
            />
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-border-subtle flex items-center gap-2">
            {footer}
          </div>
        )}
      </motion.div>
    </div>
  );
}
